import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { categories, channels } from "@/mocks/streams";
import { useFavorites } from "@/hooks/useFavorites";

const navLinkClass = ({ isActive }: { isActive: boolean }) =>
  `px-3 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
    isActive ? "text-primary-500 font-medium" : "text-foreground-500 hover:text-foreground-900"
  }`;

export default function Navbar() {
  const navigate = useNavigate();
  const { favorites } = useFavorites();
  const [catOpen, setCatOpen] = useState(false);
  const [favOpen, setFavOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const catRef = useRef<HTMLDivElement>(null);
  const favRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (catRef.current && !catRef.current.contains(target)) setCatOpen(false);
      if (favRef.current && !favRef.current.contains(target)) setFavOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const favoritesList = channels.filter((channel) => favorites.includes(channel.id));

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const query = keyword.trim();
    navigate(query ? `/search?q=${encodeURIComponent(query)}` : "/search");
    setMobileOpen(false);
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200/70 bg-background-50/90 backdrop-blur-md">
      <div className="w-full px-4 md:px-6">
        <div className="flex h-16 items-center gap-4">
          <Link to="/" className="flex items-center gap-2 whitespace-nowrap">
            <span className="w-9 h-9 flex items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-live-line text-xl"></i>
            </span>
            <span className="font-heading text-lg font-bold tracking-tight text-foreground-950">有因直播</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <NavLink to="/" className={navLinkClass} end>
              首页
            </NavLink>

            <div className="relative" ref={catRef}>
              <button
                type="button"
                onClick={() => setCatOpen((v) => !v)}
                className="flex items-center gap-1 px-3 py-2 text-sm rounded-md text-foreground-500 hover:text-foreground-900 transition-colors whitespace-nowrap"
              >
                直播分类
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? "rotate-180" : ""}`}></i>
                </span>
              </button>
              {catOpen && (
                <div className="absolute left-0 top-full mt-2 w-[420px] rounded-lg border border-background-200 bg-background-50 p-3 shadow-none">
                  <div className="grid grid-cols-2 gap-1">
                    {categories.map((cat) => (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => {
                          navigate(`/category/${cat.id}`);
                          setCatOpen(false);
                        }}
                        className="flex items-center gap-3 rounded-md px-3 py-2 text-left transition-colors hover:bg-background-100"
                      >
                        <span className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-500/12 text-primary-500">
                          <i className={`${cat.icon} text-lg`}></i>
                        </span>
                        <span className="min-w-0">
                          <span className="block text-sm text-foreground-800">{cat.name}</span>
                          <span className="block text-xs text-foreground-400">{cat.tagline}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <NavLink to="/about" className={navLinkClass}>
              关于我们
            </NavLink>
            <NavLink to="/contact" className={navLinkClass}>
              联系我们
            </NavLink>
          </nav>

          <form onSubmit={submitSearch} className="ml-auto hidden lg:flex items-center">
            <div className="flex w-64 items-center rounded-full border border-background-200 bg-background-100 px-3 py-1.5 focus-within:border-primary-400">
              <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-sm"></i>
              </span>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索主播、直播内容"
                className="w-full bg-transparent px-2 text-sm text-foreground-800 placeholder-foreground-300 outline-none"
              />
            </div>
          </form>

          <div className="ml-auto lg:ml-0 relative" ref={favRef}>
            <button
              type="button"
              onClick={() => setFavOpen((v) => !v)}
              className="relative w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-500"
              aria-label="我的收藏"
            >
              <i className="ri-heart-3-line text-lg"></i>
              {favorites.length > 0 && (
                <span className="absolute -right-1 -top-1 min-w-4 h-4 px-1 flex items-center justify-center rounded-full bg-primary-500 text-[10px] font-medium text-background-50">
                  {favorites.length}
                </span>
              )}
            </button>
            {favOpen && (
              <div className="absolute right-0 top-full mt-2 w-72 rounded-lg border border-background-200 bg-background-50 p-3">
                <p className="mb-2 px-1 text-xs text-foreground-400">我的收藏（仅本设备保存）</p>
                {favoritesList.length === 0 ? (
                  <p className="px-1 py-4 text-center text-sm text-foreground-400">还没有收藏任何频道</p>
                ) : (
                  <ul className="max-h-72 space-y-1 overflow-auto">
                    {favoritesList.map((channel) => (
                      <li key={channel.id}>
                        <button
                          type="button"
                          onClick={() => {
                            navigate(`/channel/${channel.id}`);
                            setFavOpen(false);
                          }}
                          className="flex w-full items-center gap-3 rounded-md p-2 text-left transition-colors hover:bg-background-100"
                        >
                          <span className="w-10 h-10 flex-shrink-0 overflow-hidden rounded-md">
                            <img src={channel.cover} alt={channel.title} className="w-full h-full object-cover" />
                          </span>
                          <span className="min-w-0">
                            <span className="block truncate text-sm text-foreground-800">{channel.title}</span>
                            <span className="block text-xs text-foreground-400">{channel.hostName}</span>
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-md border border-background-200 text-foreground-700"
            aria-label="展开菜单"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-background-200/70 bg-background-50 px-4 pb-5 pt-4">
          <form onSubmit={submitSearch} className="mb-4 flex items-center rounded-full border border-background-200 bg-background-100 px-3 py-2">
            <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line text-sm"></i>
            </span>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索主播、直播内容"
              className="w-full bg-transparent px-2 text-sm text-foreground-800 placeholder-foreground-300 outline-none"
            />
          </form>

          <div className="space-y-1">
            <MobileLink to="/" label="首页" onNavigate={() => setMobileOpen(false)} />
            <MobileLink to="/about" label="关于我们" onNavigate={() => setMobileOpen(false)} />
            <MobileLink to="/contact" label="联系我们" onNavigate={() => setMobileOpen(false)} />
          </div>

          <p className="mb-2 mt-4 px-1 text-xs text-foreground-400">直播分类</p>
          <div className="grid grid-cols-2 gap-1">
            {categories.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => {
                  navigate(`/category/${cat.id}`);
                  setMobileOpen(false);
                }}
                className="flex items-center gap-2 rounded-md px-3 py-2 text-left text-sm text-foreground-700 hover:bg-background-100"
              >
                <span className="w-6 h-6 flex items-center justify-center text-primary-500">
                  <i className={`${cat.icon}`}></i>
                </span>
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}

function MobileLink({ to, label, onNavigate }: { to: string; label: string; onNavigate: () => void }) {
  return (
    <Link
      to={to}
      onClick={onNavigate}
      className="block rounded-md px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:bg-background-100"
    >
      {label}
    </Link>
  );
}